import { useBranchSalesChannelCategoryGroupsQuery } from "@/generated/graphql";
import { useMemo } from "react";

const PAGE_SIZE = 200;

export interface MenuGroupBranch {
	branchID: string;
	branchName: string;
	branchCode: string;
}

// The group's own bindings: a global channel list would default to a channel
// with no branches, and the branch picker must not offer unbound branches.
export const useMenuGroupBindings = (groupID: string | undefined) => {
	// Must be memoized: a fresh variables object re-triggers urql every render.
	const options = useMemo(
		() => ({
			variables: {
				qop: {
					pagination: { page: 1, pageSize: PAGE_SIZE, withCount: false },
					filters: { salesChannelCategoryGroupID: groupID ?? null },
				},
			},
			pause: !groupID,
			requestPolicy: "network-only" as const,
			context: { suspense: false },
		}),
		[groupID],
	);

	const [{ data, fetching }] =
		useBranchSalesChannelCategoryGroupsQuery(options);

	const channelIDs = useMemo(() => {
		const seen = new Set<string>();
		for (const binding of data?.branchSalesChannelCategoryGroups.items ?? []) {
			seen.add(binding.salesChannelID);
		}
		return [...seen];
	}, [data]);

	// One group binds many categories per branch, so collapse to unique branches.
	const branchesByChannelID = useMemo(() => {
		const byChannel = new Map<string, Map<string, MenuGroupBranch>>();
		for (const binding of data?.branchSalesChannelCategoryGroups.items ?? []) {
			const branches =
				byChannel.get(binding.salesChannelID) ??
				new Map<string, MenuGroupBranch>();
			branches.set(binding.branchID, {
				branchID: binding.branchID,
				branchName: binding.branchName,
				branchCode: binding.branchCode,
			});
			byChannel.set(binding.salesChannelID, branches);
		}
		return byChannel;
	}, [data]);

	return { channelIDs, branchesByChannelID, fetching };
};
